"use client";

import { useEffect, useRef, useState } from "react";

const words = [
  { text: "API keys", color: "#c4b5fd" },
  { text: "database", color: "#93c5fd" },
  { text: "filesystem", color: "#6ee7b7" },
  { text: "customers", color: "#fbbf24" },
  { text: "emails", color: "#f9a8d4" },
  { text: "credentials", color: "#fdba74" },
];

const CHAR_DELAY = 40;
const ERASE_DELAY = 25;
const PAUSE_DISPLAY = 2200;
const PAUSE_BETWEEN = 300;

type Phase = "display" | "erasing" | "pause" | "typing";

export default function CyclingWord() {
  const [wordIndex, setWordIndex] = useState(0);
  const [charCount, setCharCount] = useState(words[0].text.length);
  const [phase, setPhase] = useState<Phase>("display");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (phase === "display") {
      timer = setTimeout(() => setPhase("erasing"), PAUSE_DISPLAY);
    } else if (phase === "pause") {
      timer = setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % words.length);
        setCharCount(0);
        setPhase("typing");
      }, PAUSE_BETWEEN);
    }

    return () => clearTimeout(timer!);
  }, [phase]);

  useEffect(() => {
    clearTimer();

    if (phase === "erasing") {
      intervalRef.current = setInterval(() => {
        setCharCount((prev) => {
          if (prev <= 1) {
            clearTimer();
            setTimeout(() => setPhase("pause"), ERASE_DELAY);
            return 0;
          }
          return prev - 1;
        });
      }, ERASE_DELAY);
    }

    if (phase === "typing") {
      const target = words[(wordIndex) % words.length].text.length;
      intervalRef.current = setInterval(() => {
        setCharCount((prev) => {
          if (prev >= target - 1) {
            clearTimer();
            setTimeout(() => setPhase("display"), CHAR_DELAY);
            return target;
          }
          return prev + 1;
        });
      }, CHAR_DELAY);
    }

    return clearTimer;
  }, [phase, wordIndex]);

  const current = words[wordIndex];
  const displayed = current.text.slice(0, charCount);
  const showCursor = phase === "erasing" || phase === "typing" || phase === "pause";

  // Reserve width for the longest word to prevent layout shift
  const maxWidth = Math.max(...words.map((w) => w.text.length));

  return (
    <span
      className="inline-block text-left"
      style={{
        color: current.color,
        minWidth: `${maxWidth}ch`,
      }}
    >
      {displayed}
      {showCursor && (
        <span
          className="inline-block w-[2px] ml-[1px] align-middle animate-pulse"
          style={{
            height: "0.85em",
            backgroundColor: current.color,
          }}
        />
      )}
    </span>
  );
}
